(function() {
    'use strict';
    
    const MAX_AGE = 30 * 60 * 1000; // 30 minutes TTL
    const HIERARCHY_KEY = 'breadcrumbsHierarchy';
    
    // Normalize any URL to a comparable pathname ("/collections/foo")
    function normalizePath(url) {
        if (!url) return '';
        
        try {
            return new URL(url, window.location.origin).pathname.replace(/\/+$/, '') || '/';
        } catch (e) {
            return '';
        }
    }
    
    function isFresh(entry) {
        return !!entry && !!entry.timestamp && (Date.now() - entry.timestamp < MAX_AGE);
    }
    
    function escapeHtml(value) {
        return String(value ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }
    
    // Session hierarchy of visited collections:
    // "/collections/handle" -> { path: [breadcrumb items], children: [showcase urls], timestamp }
    function loadHierarchy() {
        let stored = {};
        
        try {
            stored = JSON.parse(sessionStorage.getItem(HIERARCHY_KEY)) || {};
        } catch (e) {
            stored = {};
        }
        
        const hierarchy = {};
        Object.keys(stored).forEach(key => {
            if (isFresh(stored[key])) {
                hierarchy[key] = stored[key];
            }
        });
        
        return hierarchy;
    }
    
    function saveHierarchy(hierarchy) {
        try {
            sessionStorage.setItem(HIERARCHY_KEY, JSON.stringify(hierarchy));
        } catch (e) {
            // Storage can be unavailable (private mode / quota) — breadcrumbs still render
        }
    }
    
    // Build breadcrumb HTML from path array
    function buildBreadcrumbHTML(breadcrumbPath) {
        const homeLink = document.querySelector('.breadcrumbs .items li:first-child a');
        const homeURL = homeLink?.getAttribute('href') || '/';
        const homeTitle = homeLink?.querySelector('strong')?.textContent || 'Home';
        
        let html = `<li class="item slider__slide"><a href="${escapeHtml(homeURL)}"><strong>${escapeHtml(homeTitle)}</strong></a><i class="arrow right"></i></li>`;
        
        breadcrumbPath.forEach((item, index) => {
            const isLast = index === breadcrumbPath.length - 1;
            if (isLast) {
                html += `<li class="item slider__slide breadcrumb-collection-items">${escapeHtml(item.title)}</li>`;
            } else {
                html += `<li class="item slider__slide"><strong><a href="${escapeHtml(item.url)}">${escapeHtml(item.title)}</a></strong><i class="arrow right"></i></li>`;
            }
        });
        
        return html;
    }
    
    function renderBreadcrumbs(container, breadcrumbPath) {
        const ulElement = container?.closest('ul.items');
        if (!ulElement) return;
        
        ulElement.outerHTML = `<ul class="items slider slider--desktop">${buildBreadcrumbHTML(breadcrumbPath)}</ul>`;
    }
    
    // Sub-collections from the Collection Showcase metafield, rendered by sections/breadcrumbs.liquid
    function readShowcaseChildren() {
        const element = document.getElementById('breadcrumb-collection-children');
        if (!element) return [];
        
        try {
            const children = JSON.parse(element.textContent);
            
            return Array.isArray(children) ? children.map(normalizePath).filter(Boolean) : [];
        } catch (e) {
            return [];
        }
    }
    
    // Extract breadcrumb path from sidebar for a given link
    function extractBreadcrumbPath(activeLink) {
        const breadcrumbPath = [];
        const isGrandchild = activeLink.closest('.sidebar-menu__grandchild-menu');
        const isChild = activeLink.closest('.sidebar-menu__child-menu');
        
        if (isGrandchild) {
            // Third level: Grandparent > Parent > Child
            const childTitle = activeLink.textContent.trim();
            const childUrl = activeLink.getAttribute('href');
            
            const parentDetails = activeLink.closest('.sidebar-menu__inner-grandchild-menu')?.parentElement;
            const parentLink = parentDetails?.querySelector(':scope > summary a');
            
            const grandparentDetails = activeLink.closest('.sidebar-menu__submenu')?.parentElement;
            const grandparentLink = grandparentDetails?.querySelector(':scope > summary a');
            
            if (grandparentLink) {
                breadcrumbPath.push({
                    url: grandparentLink.getAttribute('href'),
                    title: grandparentLink.textContent.trim()
                });
            }
            if (parentLink) {
                breadcrumbPath.push({
                    url: parentLink.getAttribute('href'),
                    title: parentLink.textContent.trim()
                });
            }
            breadcrumbPath.push({ url: childUrl, title: childTitle });
            
        } else if (isChild) {
            // Second level: Parent > Child
            const childTitle = activeLink.textContent.trim();
            const childUrl = activeLink.getAttribute('href');
            const isSummaryLink = activeLink.closest('summary');
            
            if (isSummaryLink) {
                // This is a parent being viewed
                const grandparentDetails = activeLink.closest('.sidebar-menu__submenu')?.parentElement;
                const grandparentLink = grandparentDetails?.querySelector(':scope > summary a');
                
                if (grandparentLink) {
                    breadcrumbPath.push({
                        url: grandparentLink.getAttribute('href'),
                        title: grandparentLink.textContent.trim()
                    });
                }
            } else {
                // This is a child under a parent
                const parentDetails = activeLink.closest('.sidebar-menu__child-menu')?.parentElement?.parentElement?.parentElement;
                const parentLink = parentDetails?.querySelector(':scope > summary a');
                
                if (parentLink) {
                    breadcrumbPath.push({
                        url: parentLink.getAttribute('href'),
                        title: parentLink.textContent.trim()
                    });
                }
            }
            breadcrumbPath.push({ url: childUrl, title: childTitle });
            
        } else {
            // First level (top level item)
            breadcrumbPath.push({
                url: activeLink.getAttribute('href'),
                title: activeLink.textContent.trim()
            });
        }
        
        return breadcrumbPath;
    }
    
    function getSidebarBreadcrumbPath(pathName) {
        const sidebar = document.querySelector('.sidebar-menu');
        if (!sidebar) return null;
        
        for (const link of sidebar.querySelectorAll('a[href]')) {
            if (link.getAttribute('href') === pathName) {
                const breadcrumbPath = extractBreadcrumbPath(link);
                
                if (breadcrumbPath.length > 0) {
                    return breadcrumbPath;
                }
            }
        }
        
        return null;
    }
    
    // Find the most recently visited collection whose Collection Showcase lists the current collection
    function findParentEntry(hierarchy, currentPath) {
        const parentKeys = Object.keys(hierarchy).filter(key => {
            const entry = hierarchy[key];
            const lastItem = entry.path?.[entry.path.length - 1];
            
            return key !== currentPath && entry.children?.includes(currentPath) && lastItem && normalizePath(lastItem.url) === key;
        });
        
        if (parentKeys.length === 0) return null;
        
        return parentKeys
            .map(key => hierarchy[key])
            .sort((a, b) => b.timestamp - a.timestamp)[0];
    }
    
    // Handle product page breadcrumbs
    function handleProductPage(pathName, breadcrumbsWrapper) {
        const productBreadcrumbContainer = document.querySelector('.items.slider.slider--desktop');
        const productTitle = document.querySelector('.breadcrumbs .items li:last-child')?.textContent.trim();
        
        if (!productBreadcrumbContainer || !productTitle) {
            breadcrumbsWrapper?.classList.add('js-ready');
            return;
        }
        
        // Only the collection the product was opened from
        const referrerEntry = loadHierarchy()[normalizePath(document.referrer)];
        
        if (referrerEntry?.path?.length) {
            renderBreadcrumbs(productBreadcrumbContainer, [
                ...referrerEntry.path,
                { url: pathName, title: productTitle }
            ]);
        }
        
        breadcrumbsWrapper?.classList.add('js-ready');
    }
    
    // Handle collection page breadcrumbs
    function handleCollectionPage(pathName, breadcrumbsWrapper) {
        const collectionBreadcrumbContainer = document.querySelector('.breadcrumb-collection-items');
        
        if (!collectionBreadcrumbContainer) {
            breadcrumbsWrapper?.classList.add('js-ready');
            return;
        }
        
        const children = readShowcaseChildren();
        const hierarchy = loadHierarchy();
        const currentItem = { url: pathName, title: collectionBreadcrumbContainer.textContent.trim() };
        
        // 1) Collection is in the sidebar tree — original behaviour
        let breadcrumbPath = getSidebarBreadcrumbPath(pathName);
        
        // 2) Direct navigation from a parent collection (Collection Showcase / featured collection)
        if (!breadcrumbPath) {
            const referrerEntry = hierarchy[normalizePath(document.referrer)];
            
            if (referrerEntry?.children?.includes(pathName)) {
                breadcrumbPath = [...referrerEntry.path, currentItem];
            }
        }
        
        // 3) Previously resolved path for this collection (reload / back navigation)
        if (!breadcrumbPath) {
            const cachedEntry = hierarchy[pathName];
            
            if (cachedEntry?.path?.length > 1) {
                breadcrumbPath = cachedEntry.path;
            }
        }
        
        // 4) Any visited parent collection from the Collection Showcase hierarchy
        if (!breadcrumbPath) {
            const parentEntry = findParentEntry(hierarchy, pathName);
            
            if (parentEntry) {
                breadcrumbPath = [...parentEntry.path, currentItem];
            }
        }
        
        if (breadcrumbPath) {
            renderBreadcrumbs(collectionBreadcrumbContainer, breadcrumbPath);
        }
        
        // Remember the resolved path and sub-collections, so child collections
        // can build their breadcrumbs without chaining unrelated pages
        if (breadcrumbPath || children.length > 0) {
            hierarchy[pathName] = {
                path: breadcrumbPath || [currentItem],
                children,
                timestamp: Date.now()
            };
            saveHierarchy(hierarchy);
        }
        
        breadcrumbsWrapper?.classList.add('js-ready');
    }
    
    // Main function
    function buildBreadcrumbsFromSidebar() {
        const pathName = normalizePath(window.location.pathname);
        const breadcrumbsWrapper = document.querySelector('.breadcrumbs');
        
        if (pathName.includes('/products/')) {
            handleProductPage(pathName, breadcrumbsWrapper);
        } else if (pathName.includes('/collections/')) {
            handleCollectionPage(pathName, breadcrumbsWrapper);
        } else {
            breadcrumbsWrapper?.classList.add('js-ready');
        }
    }
    
    // Run when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', buildBreadcrumbsFromSidebar);
    } else {
        buildBreadcrumbsFromSidebar();
    }
})();
